/** api-todos.ts — 할 일 관리 API */
import type { Todo } from './types';
import type { Staff } from './components/AssigneeChipPicker';

const BASE = '/api/todos';

function token(): string { return localStorage.getItem('token') || ''; }

async function req<T>(method: string, path: string, body?: unknown): Promise<{ success: boolean; data?: T; error?: string }> {
  const opts: RequestInit = {
    method, cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() },
  };
  if (body && method !== 'GET') opts.body = JSON.stringify(body);
  const res = await fetch(BASE + path, opts);
  if (res.status === 401) return { success: false, error: '인증 필요' };
  const json = await res.json();
  return json;
}

export interface ListFilters {
  assigned_to?: string;
  user_id?: string;
  mine?: boolean;
  /** 내가 남에게 맡긴 일만 (2026-10-23) */
  given?: boolean;
  status?: 'all' | 'open' | 'pending' | 'partial' | 'review' | 'notify' | 'done';
  search?: string;
  page?: number;
  limit?: number;
}

export async function getTodos(f: ListFilters = {}): Promise<{ success: boolean; data?: { items: Todo[]; total: number; page: number; limit: number }; error?: string }> {
  const q = new URLSearchParams();
  if (f.assigned_to) q.set('assigned_to', f.assigned_to);
  if (f.user_id) q.set('user_id', f.user_id);
  if (f.mine) q.set('mine', 'true');
  if (f.given) q.set('given', '1');
  if (f.status) q.set('status', f.status);
  if (f.search) q.set('search', f.search);
  if (f.page) q.set('page', String(f.page));
  if (f.limit) q.set('limit', String(f.limit));
  q.set('_', String(Date.now()));
  return req('GET', '?' + q.toString());
}

export async function getPendingForMe(): Promise<{ success: boolean; data?: Todo[]; error?: string }> {
  return req('GET', '/pending-for-me?_=' + Date.now());
}

/** 내가 오늘 끝낸 것(한국 날짜) — 휴대폰 「오늘 할 일」 */
export async function getDoneToday(): Promise<{ success: boolean; data?: Todo[]; error?: string }> {
  return req('GET', '/done-today?_=' + Date.now());
}

export async function getTemplates(): Promise<{ success: boolean; data?: Todo[]; error?: string }> {
  return req('GET', '/templates?_=' + Date.now());
}

export async function getStaff(): Promise<{ success: boolean; data?: Staff[]; error?: string }> {
  return req('GET', '/staff');
}

export async function createTodo(body: {
  title: string; description?: string; assignees?: string[]; due_date?: string | null;
  is_template?: boolean; recur_type?: string | null; recur_days?: string | null;
  needs_check?: boolean; notify_to?: string | null; notify_phone?: string | null;
}): Promise<{ success: boolean; data?: Todo; error?: string }> {
  return req('POST', '', body);
}

/* ===== 결과 확인 흐름 (2026-10-22) ===== */
type Res<T> = { success: boolean; data?: T; error?: string };
/** 담당자 「결과 적기」 — 안내받을 분 · 연락처도 같이 고칠 수 있다 */
export async function writeResult(id: string, body: { note: string; notify_to?: string | null; notify_phone?: string | null }): Promise<Res<Todo>> {
  return req('POST', `/${id}/result`, body);
}
/** 맡긴 사람 확인 — 누가 안내할지 고르거나 다시 봐 달라고 돌려보냄 */
export async function checkTodo(id: string, body: ({ action: 'ok'; notify_by: 'staff' | 'me'; memo?: string } | { action: 'done' } | { action: 'back'; reason: string }) & { seen?: string | null }): Promise<Res<Todo>> {
  return req('POST', `/${id}/check`, body);
}
/** 「답 기다리는 중」 — 어디에 물었는지 + 다시 볼 날(YYYY-MM-DD) */
export async function waitReply(id: string, body: { where: string; until: string }): Promise<Res<Todo>> {
  return req('POST', `/${id}/wait`, body);
}
/** 「안내했어요」 → 완료 */
export async function markNotified(id: string): Promise<Res<Todo>> {
  return req('POST', `/${id}/notified`, {});
}
export interface ContactPick { id: string; name: string; phone: string; company: string; kind: string }
/** 안내받을 분 후보(FP 관리 · 이름으로) */
export async function findContacts(id: string, q: string): Promise<Res<ContactPick[]>> {
  return req('GET', `/${id}/contacts?q=${encodeURIComponent(q)}&_=${Date.now()}`);
}

export async function updateTodo(id: string, body: Partial<Todo>): Promise<{ success: boolean; data?: Todo; error?: string }> {
  return req('PUT', '/' + id, body);
}

export async function deleteTodo(id: string): Promise<{ success: boolean; error?: string }> {
  return req('DELETE', '/' + id);
}

export async function uploadAttachment(todoId: string, file: File): Promise<{ success: boolean; data?: import('./types').TodoAttachment; error?: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${BASE}/${todoId}/attachments`, {
    method: 'POST',
    headers: { Authorization: 'Bearer ' + token() },
    body: fd,
  });
  return res.json();
}

export async function getAttachmentUrl(todoId: string, attId: string): Promise<{ success: boolean; data?: { url: string }; error?: string }> {
  return req('GET', `/${todoId}/attachments/${attId}/url?_=` + Date.now());
}

export async function deleteAttachment(todoId: string, attId: string): Promise<{ success: boolean; error?: string }> {
  return req('DELETE', `/${todoId}/attachments/${attId}`);
}

// ===== 댓글 스레드 =====
import type { TodoComment } from './types';

export async function getComments(todoId: string): Promise<{ success: boolean; data?: TodoComment[]; error?: string }> {
  return req('GET', `/${todoId}/comments?_=` + Date.now());
}

export async function postComment(todoId: string, content: string, files: File[]): Promise<{ success: boolean; data?: TodoComment; error?: string }> {
  const fd = new FormData();
  fd.append('content', content);
  for (const f of files) fd.append('files', f);
  const res = await fetch(`${BASE}/${todoId}/comments`, {
    method: 'POST',
    headers: { Authorization: 'Bearer ' + token() },
    body: fd,
  });
  return res.json();
}

export async function deleteComment(todoId: string, commentId: string): Promise<{ success: boolean; error?: string }> {
  return req('DELETE', `/${todoId}/comments/${commentId}`);
}

export async function getCommentAttachmentUrl(todoId: string, commentId: string, attId: string): Promise<{ success: boolean; data?: { url: string }; error?: string }> {
  return req('GET', `/${todoId}/comments/${commentId}/attachments/${attId}/url?_=` + Date.now());
}

export async function editComment(todoId: string, commentId: string, content: string): Promise<{ success: boolean; data?: TodoComment; error?: string }> {
  return req('PUT', `/${todoId}/comments/${commentId}`, { content });
}
